import toast from 'react-hot-toast';
import i18n from '../i18n';
import { useAuthStore } from '../store/authStore';
import { getActiveAccountHeader, getEffectivePlan } from '../store/accountStore';
import { useUpgradeGateStore } from '../store/upgradeGateStore';
import { normalizeTier, type FeatureKey, type EffectiveTier } from '../constants/planLimits';

/**
 * Global 401 + 403 (plan-gate) interceptor.
 *
 * Wraps `window.fetch` exactly once at app startup. Two responsibilities:
 *
 *   1. HTTP 401 on an authenticated request → dead session. Single
 *      toast + clean logout + redirect to /login?redirect=<here>.
 *   2. HTTP 403 (or 4xx) with a structured plan-gate body
 *      `{ feature, requiredPlan }` → open the global UpgradeGateModal.
 *      Lets every existing fetch site (incl. dozens of inline ones)
 *      surface upgrade prompts for free without per-callsite changes.
 *
 * Why intercept at the `window.fetch` layer instead of refactoring
 * every call site to use a wrapper:
 *
 *   - The codebase has dozens of inline `fetch(...)` calls scattered
 *     across pages, stores, and components. A wrapper would require
 *     touching every file (huge surface for regressions). Intercepting
 *     here gives us blanket coverage with zero behavioural change for
 *     the common case (non-401 responses are passed through untouched).
 *
 *   - Future fetches added by anyone — including third-party libraries
 *     — automatically inherit the protection.
 *
 * Behaviours we explicitly avoid:
 *
 *   - Don't react to 401s on the auth endpoints themselves
 *     (login/register/refresh) — a 401 there means "wrong creds" or
 *     "stale token" and the caller already has a UX for that.
 *   - Don't react to 401s on requests that didn't include a Bearer
 *     token — those are public endpoints; 401 there is the server's
 *     business, not a session-expiry signal.
 *   - Don't fire if the user isn't authenticated in the store (avoids a
 *     redirect-to-login loop while already on /login).
 *   - Dedupe concurrent 401s with `logoutInFlight` so a screen with
 *     five parallel requests doesn't show five toasts and trigger five
 *     redirects.
 */

const ORIGINAL_FETCH = window.fetch.bind(window);

// Endpoints whose 401 means "bad credentials" or "stale token", not
// "active session just expired" — they handle their own error UX.
const AUTH_BYPASS_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
];

let logoutInFlight = false;

function urlString(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function getAuthHeader(input: RequestInfo | URL, init?: RequestInit): string | null {
  // init.headers wins over Request.headers when both exist (matches
  // the real fetch precedence).
  if (init?.headers) {
    const h = new Headers(init.headers);
    return h.get('Authorization') || h.get('authorization');
  }
  if (input instanceof Request) {
    return input.headers.get('Authorization') || input.headers.get('authorization');
  }
  return null;
}

async function handleSessionExpired() {
  if (logoutInFlight) return;
  logoutInFlight = true;

  try {
    toast.error(i18n.t('common:errors.sessionExpired'), {
      id: 'session-expired',
      duration: 4000,
    });
  } catch {
    // i18n not ready yet — surface a plain English fallback instead of
    // silently logging the user out with no explanation.
    try {
      toast.error('Your session has expired. Please log in again.', {
        id: 'session-expired',
        duration: 4000,
      });
    } catch {
      /* toast unavailable in this context (SSR / very early boot) */
    }
  }

  try {
    await useAuthStore.getState().logout();
  } catch (e) {
    console.error('Logout during 401 handling failed:', e);
  }

  // Don't redirect if we're already on the login page (or any public
  // route that doesn't need auth) — avoids a flash and lets the user
  // see the toast.
  const currentPath = window.location.pathname;
  const onAuthScreen =
    currentPath === '/login' ||
    currentPath === '/register' ||
    currentPath === '/forgot-password' ||
    currentPath === '/reset-password' ||
    currentPath === '/' ||
    currentPath.startsWith('/legal') ||
    currentPath.startsWith('/privacy') ||
    currentPath.startsWith('/terms');

  if (!onAuthScreen) {
    const returnTo = currentPath + window.location.search;
    window.location.replace(`/login?redirect=${encodeURIComponent(returnTo)}`);
  }
}

/**
 * Server-side `requireFeature` middleware returns this shape on 403.
 * We accept anything with `feature` + `requiredPlan` to stay forward-
 * compatible (extra fields are ignored).
 */
interface PlanGatePayload {
  error?: string;
  feature?: string;
  plan?: string;
  requiredPlan?: string;
  upgradeUrl?: string;
  message?: string;
}

function looksLikePlanGate(body: unknown): body is PlanGatePayload {
  if (!body || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  return typeof b.feature === 'string' && typeof b.requiredPlan === 'string';
}

/**
 * Read a 403 body without consuming the original response stream — we
 * MUST clone first because almost every callsite does `.json()` after
 * the await, and `Response` body can only be read once.
 *
 * Returns null if the body isn't valid JSON or doesn't look like a
 * plan-gate response (the more common case — most 403s are auth/RLS).
 */
async function tryReadPlanGate(response: Response): Promise<PlanGatePayload | null> {
  const ct = response.headers.get('content-type') || '';
  if (!ct.includes('application/json')) return null;
  try {
    const cloned = response.clone();
    const body = await cloned.json();
    return looksLikePlanGate(body) ? body : null;
  } catch {
    return null;
  }
}

async function handlePlanGate(payload: PlanGatePayload) {
  // Use the active workspace owner's plan (not the member's personal
  // Free tier) so upgrade modals match what the API just enforced via
  // req.accountId.
  const currentPlan: EffectiveTier = normalizeTier(getEffectivePlan());
  const requiredPlan: EffectiveTier = normalizeTier(payload.requiredPlan);

  useUpgradeGateStore.getState().open({
    feature: payload.feature as FeatureKey,
    requiredPlan,
    currentPlan,
    message: payload.message,
    upgradeUrl: payload.upgradeUrl,
  });
}

export function installApiInterceptor() {
  // Idempotent: avoid double-wrapping on hot reload.
  if ((window.fetch as unknown as { __hitzlinkPatched?: boolean }).__hitzlinkPatched) {
    return;
  }

  const patched: typeof window.fetch = async (input, init) => {
    // ── Team workspace context ──
    // When the user has switched into another owner's workspace, stamp
    // the active account id on every authed API request so the server's
    // resolveAccount middleware scopes data to that workspace. On the
    // user's OWN account this returns null and we don't touch the request
    // (keeps owner traffic identical to the pre-team behaviour).
    const accountId = getActiveAccountHeader();
    if (accountId) {
      const authHeader = getAuthHeader(input as RequestInfo, init);
      const url = urlString(input as RequestInfo | URL);
      const isAuthed = !!authHeader && authHeader.startsWith('Bearer ');
      const isAuthPath = AUTH_BYPASS_PATHS.some((p) => url.includes(p));
      if (isAuthed && !isAuthPath) {
        const headers = new Headers(
          init?.headers ?? (input instanceof Request ? input.headers : undefined)
        );
        headers.set('X-Account-Id', accountId);
        init = { ...(init || {}), headers };
      }
    }

    const response = await ORIGINAL_FETCH(input as RequestInfo, init);

    // Fast path: anything outside our error window passes straight
    // through with no JSON parse cost.
    if (response.status !== 401 && response.status !== 403) return response;

    const authHeader = getAuthHeader(input as RequestInfo, init);
    const isAuthed = !!authHeader && authHeader.startsWith('Bearer ');
    const url = urlString(input as RequestInfo | URL);
    const isAuthPath = AUTH_BYPASS_PATHS.some((p) => url.includes(p));

    // ── 401 → session expired ──
    if (response.status === 401) {
      if (!isAuthed) return response;
      if (isAuthPath) return response;
      if (!useAuthStore.getState().isAuthenticated) return response;
      void handleSessionExpired();
      return response;
    }

    // ── 403 → either plan gate or generic forbid ──
    // Only inspect plan-gate bodies on authed app requests; public
    // endpoints can 403 for many reasons that aren't paywalls.
    if (!isAuthed) return response;
    if (isAuthPath) return response;
    const gate = await tryReadPlanGate(response);
    if (gate) {
      void handlePlanGate(gate);
    }
    return response;
  };

  (patched as unknown as { __hitzlinkPatched?: boolean }).__hitzlinkPatched = true;
  window.fetch = patched;
}
